import { useEffect, useState } from "react";
import { useRegion } from "hooks";
import type { RelatedVariant } from "@shared/graphql.types";
import { track } from "tracking";
import { Logger } from "@shared/utils.logger";
import { navigateTo } from "@shared/utils.routing";

const findationApiKeys = {
  uk: process.env.NEXT_PUBLIC_FINDATION_UK_KEY,
  us: process.env.NEXT_PUBLIC_FINDATION_US_KEY
} as const;

type SearchCompleteData = {
  eventName: "searchComplete";
  countShadesEntered: number | null;
  matchSuccess: boolean | null;
  shadesEntered:
    | {
        fullName: string;
        shadeName: string;
        productName: string;
        brandName: string;
      }[]
    | null;
  userId: string | null;
  recommendedShade: string | null;
  // Slug of the product page corresponding to the recommended shade, not SKU
  externalId: string | null;
  [key: string]: unknown;
};

interface FindationAPI {
  init: (
    el: HTMLElement,
    apiKey: string,
    options?: Record<string, unknown>
  ) => void;
  widgetEmbedded: () => void;
  openWidget: () => void;
  widgetDidOpen: (callback?: () => void) => void;
  closeWidget: () => void;
  widgetDidClose: () => void;
  onSearchComplete?: (data: SearchCompleteData) => void;
  onActionReceived?: (
    payload: string | number | Record<string, unknown>
  ) => void;
  [key: string]: unknown;
  resultsWillRender?: (html: string) => void;
}

declare global {
  interface Window {
    Findation?: FindationAPI;
  }
}

let initDone = false;
let loadPromise: Promise<void> | null = null;
let container: HTMLDivElement | null = null;

export type FindationProps = {
  currentSku: string;
  relatedVariants?: RelatedVariant[];
  options?: {
    productId?: string;
    buttonWidth?: string;
    buttonHeight?: string;
    isDemoMode?: boolean;
  };
};

const defaultOptions = {
  buttonWidth: "0px",
  buttonHeight: "0px",
  isDemoMode: false
};

function getVariantIdFromExternalId(
  externalId = "",
  relatedVariants: RelatedVariant[] = []
): string {
  if (!externalId || !relatedVariants?.length) {
    return externalId || "unknown";
  }
  // Extract slug from external ID (e.g., "/products/futurelipstick-luxe-shine-baby-berry" -> "futurelipstick-luxe-shine-baby-berry")
  const slug = externalId.replace(/^\/products\//, "");
  const matchingVariant = relatedVariants.find(
    (variant) => variant.slug === slug
  );

  return matchingVariant?.id || externalId;
}

function toSlugOnly(path: string): string {
  return path?.replace(/\/+$/, "")?.split("/")?.pop() ?? "";
}

export function useFindation({
  options,
  currentSku,
  relatedVariants
}: FindationProps) {
  options = { ...defaultOptions, ...(options ?? {}) };

  const region = useRegion(); // get the current region
  const apiKey = findationApiKeys[region as keyof typeof findationApiKeys];

  const [showButton, setShowButton] = useState(false);
  const [findation, setFindation] = useState<Window["Findation"] | null>(null);
  const [searchCompleteState, setSearchCompleteState] =
    useState<SearchCompleteData | null>(null);

  useEffect(() => {
    try {
      if (!apiKey) {
        throw new Error("Findation API key is not defined for this region");
      }

      // Load script only once
      if (!loadPromise) {
        loadPromise = new Promise<void>((resolve, reject) => {
          if (window.Findation) return resolve();
          const style = document.createElement("style");
          style.textContent = `#findation-button-iframe { display: none !important; }`;
          document.head.appendChild(style);
          container = document.createElement("div");
          container.id = "findation-widget-button";
          container.style.display = "none";
          document.body.appendChild(container);
          const script = document.createElement("script");
          script.src =
            "https://assets.findation.com/javascripts/w-adv-13.min.js";
          script.async = true;
          script.onload = () => resolve();
          script.onerror = reject;
          document.body.appendChild(script);
        });
      }

      loadPromise.then(() => {
        if (window.Findation && container) {
          if (!initDone) {
            initDone = true;
            window.Findation.init(container, apiKey, options);
          }
          if (!findation) setFindation(window.Findation);
        }
      });

      const handleMessage = (event: MessageEvent) => {
        if (!event.origin.includes("findation.com")) return;
        if (event.data === "buttonLoadSuccess") {
          setShowButton(true);
        }
      };
      window.addEventListener("message", handleMessage);

      return () => window.removeEventListener("message", handleMessage);
    } catch (error) {
      Logger.warn("Error initializing Findation:", error);
      track({
        event: "Shade Matcher Loaded",
        properties: {
          status: "FAILED",
          sku: currentSku
        }
      });
    }
  }, [apiKey, options, region, findation, currentSku]);

  useEffect(() => {
    if (!findation) return;

    findation.onSearchComplete = (data: SearchCompleteData) => {
      setSearchCompleteState(data);
      track({
        event: "Shade Matcher Search Completed",
        properties: {
          countShadesEntered: data?.countShadesEntered || 0,
          externalId: data?.externalId || "",
          matchSuccess: !!data?.matchSuccess,
          recommendedShade: data?.recommendedShade || "",
          shadesEntered: data?.shadesEntered || [],
          sku: getVariantIdFromExternalId(
            data?.externalId ?? "",
            relatedVariants
          )
        }
      });
    };

    findation.resultsWillRender = (html: string) => {
      if (!relatedVariants?.length) return html;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const largeBoxElement = doc.querySelector("p.large-box");
      // Find the circle SVG within the large box
      const svgElement = largeBoxElement?.querySelector("svg");

      if (!svgElement || !largeBoxElement) return html;

      const shadeText = largeBoxElement.textContent?.trim() || "";
      const matchingVariant = relatedVariants.find((variant) => {
        const variantShadeName = variant.title?.split("(")[0].trim();
        return (
          variantShadeName &&
          shadeText &&
          (shadeText.includes(variantShadeName) ||
            variantShadeName.includes(shadeText))
        );
      });

      // Replace SVG with image if matching variant found
      if (matchingVariant?.shadeVisual) {
        const img = Object.assign(doc.createElement("img"), {
          src: matchingVariant.shadeVisual,
          alt: shadeText,
          style:
            "width: 40px; height: 40px; border-radius: 50%; object-fit: cover;"
        });
        svgElement.parentNode?.replaceChild(img, svgElement);
      }

      return doc.documentElement.outerHTML;
    };

    findation.onActionReceived = (action) => {
      if (action === "shopThisShade" && searchCompleteState?.externalId) {
        track({
          event: "Page Element Clicked",
          properties: {
            location_type: "shade_matcher",
            module_content: "shade_matcher",
            module_content2: "shopThisShade",
            module_content_details: searchCompleteState.externalId
          }
        });
        navigateTo(`/products/${toSlugOnly(searchCompleteState.externalId)}`);
      }
      if (action === "addAnotherProduct" || action === "startAgain") {
        track({
          event: "Page Element Clicked",
          properties: {
            location_type: "shade_matcher",
            module_content: "shade_matcher",
            module_content2: action
          }
        });
      }
    };

    findation.widgetDidOpen = () => {
      track({
        event: "Page Element Clicked",
        properties: {
          location_type: "shade_matcher",
          module_content: "shade_matcher",
          module_content_details: "opened",
          module_content_details2: currentSku
        }
      });
    };

    findation.widgetDidClose = () => {
      track({
        event: "Page Element Clicked",
        properties: {
          location_type: "shade_matcher",
          module_content: "shade_matcher",
          module_content_details: "closed",
          module_content_details2: currentSku
        }
      });
    };

    findation.widgetEmbedded = function () {
      track({
        event: "Shade Matcher Loaded",
        properties: {
          status: "SUCCEEDED",
          sku: currentSku
        }
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSku, findation, relatedVariants, searchCompleteState]);

  return { showButton, findation };
}
